import type { Ref } from 'vue'
import { computed } from 'vue'

import type {
  ActivityGroup,
  ActivityGroupRelation,
  ActivityRelationSettings,
  RelationType,
} from '@/types'
import useLinkActivity from '@/components/ActivityLink/use/useActivityLink'
import type { RelationsContainer } from '@/components/ActivityLink/ActivityLink.types'
import { ActivityLinkActivityTarget } from '@/components/ActivityLink/ActivityLinkTarget'
import { Relation } from '@/components/ActivityLink/Relation'
import { useActivityGroupService } from './services'

export default function useActivityGroup(activityGroup: Ref<ActivityGroup>) {
  const { service } = useActivityGroupService()

  const container = computed<
    RelationsContainer<ActivityLinkActivityTarget, ActivityGroupRelation>
  >(() => {
    return {
      id: activityGroup.value.id,
      relations: activityGroup.value.relations.map(
        (relation) =>
          new Relation(
            relation.id,
            new ActivityLinkActivityTarget(relation.leftId),
            new ActivityLinkActivityTarget(relation.rightId),
            relation.type,
            relation.settings
          )
      ),
      activities: activityGroup.value.activities.map(
        (activityGroupActivity) => {
          return {
            id: activityGroupActivity.id,
            name: activityGroupActivity.activity.name,
            type: activityGroupActivity.activity.type,
          }
        }
      ),
      link(payload: {
        left: ActivityLinkActivityTarget
        right: ActivityLinkActivityTarget
        type: RelationType
        settings?: ActivityRelationSettings
      }) {
        return service.link({
          activityGroupId: activityGroup.value.id,
          leftId: payload.left.id,
          rightId: payload.right.id,
          type: payload.type,
          settings: payload.settings,
        })
      },
      linkBulk(
        payload: {
          left: ActivityLinkActivityTarget
          right: ActivityLinkActivityTarget
          type: RelationType
          settings?: ActivityRelationSettings
        }[]
      ) {
        return service.linkBulk(
          payload.map((item) => ({
            activityGroupId: activityGroup.value.id,
            leftId: item.left.id,
            rightId: item.right.id,
            type: item.type,
            settings: item.settings,
          }))
        )
      },
      update(payload: {
        id: number
        right: ActivityLinkActivityTarget | null
        settings?: ActivityRelationSettings
      }) {
        return service.updateLink(payload.id, {
          rightId: payload.right?.id,
          settings: payload.settings,
        })
      },
      async unlink(id: number) {
        await service.unlink(id)
      },
    }
  })
  const { link } = useLinkActivity(container)

  return {
    container,
    link,
  }
}
